import { useEffect, useMemo, useState } from "react";
import { fileTypeFromStream } from "file-type";
import FileInfo from "files/types/interfaces/fileInfo";
import mammoth from "mammoth/mammoth.browser";
import { Mime } from "mime";
import other from "mime/types/other.js";
// .js extension is necessary here due to weird export config from mime lib
import standard from "mime/types/standard.js";
import { read, utils } from "xlsx";
import monitoring from "~/utils/monitoring";

const { VITE_RWB_API } = import.meta.env;

export const DEFAULT_ERROR_MESSAGE = "Unable to render file preview";

const mime = new Mime(standard, other);
// Extend mime-db types for source code files
mime.define(
  {
    "text/x-python": ["py"],
    "text/x-sas": ["sas"],
    "text/x-rstudio": ["r"],
    "text/x-sql": ["sql"],
    "text/json": ["json"],
  },
  true
);

// 200 MB cap for all file previews
const MAX_SIZE = 200 * 1000 * 1000;

type PreviewType = "text" | "pdf" | "image";
type DecodeFn = (data: Uint8Array) => Promise<string>;

type PreviewFile = Pick<FileInfo, "key" | "size">;

interface PreviewResponse {
  loading: boolean;
  error?: string;
  progress?: number;
  data?: {
    type: PreviewType;
    content: string;
  } | null;
}

/**
 * Determines the MIME type of a file by either using the provided Content-Type header,
 * inferring from the file's magic bytes, or falling back to the file extension.
 *
 * @param downloadUrl - The URL to download the file from S3.
 * @param extension - The file extension (optional).
 * @returns A promise that resolves to the determined MIME type as a string.
 */
const determineMimeType = async (downloadUrl: string, extension = ""): Promise<string> => {
  let contentType = "";

  const response = await fetch(downloadUrl, { headers: { Range: "bytes=0-2048" } });
  const originalType = response.headers.get("Content-Type")?.toLowerCase() ?? "";

  if (originalType && !originalType.endsWith("octet-stream")) {
    // Use original S3 MIME type if it's set to something specific
    contentType = originalType;
  } else {
    // Check magic header bytes to infer the MIME type
    // see: https://en.wikipedia.org/wiki/List_of_file_signatures
    const byteInferredType = await fileTypeFromStream(response.body!);
    // Fall back to guessing through file extension
    // note: Files are not required to have an extension and extensions can be misleading
    const extensionInferredType = mime.getType(extension);

    if (byteInferredType) {
      contentType = byteInferredType.mime;
    } else if (extensionInferredType) {
      contentType = extensionInferredType;
    }
  }

  // Per SCSM-1690, coerce renderable text formats from application/* to text/*
  if (contentType.startsWith("application/")) {
    switch (extension) {
      case "csv":
        contentType = "text/csv";
        break;
      case "tsv":
        contentType = "text/tab-separated-values";
        break;
      case "rtf":
        contentType = "text/rtf";
        break;
      case "json":
        contentType = "text/json";
        break;
      case "yml":
      case "yaml":
        contentType = "text/yaml";
        break;
    }
  } else {
    if (extension === "nf" || extension === "wdl") {
      contentType = "text/json";
    }
  }

  // Remove any potential character encoding and just return the MIME type
  return contentType.split(";")[0].trim();
};

/**
 * Determine what general type of renderer should be used, as well as how the data should be decoded/loaded from response
 */
const getPreviewHandler = (mimeType: string): { type: PreviewType; decoder: DecodeFn } | null => {
  if (mimeType.startsWith("text/") || mimeType === "application/json") {
    return {
      type: "text",
      decoder: async (data: Uint8Array) => {
        return new TextDecoder("utf-8").decode(data);
      },
    };
  }

  // Microsoft Word document
  if (mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
    return {
      type: "text",
      decoder: async (data: Uint8Array) => {
        const markup = await mammoth.convertToHtml({ arrayBuffer: data.buffer });
        return markup.value;
      },
    };
  }

  // Microsoft Excel spreadsheet
  if (
    [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ].includes(mimeType)
  ) {
    return {
      type: "text",
      decoder: async (data: Uint8Array) => {
        const workbook = read(data.buffer, { type: "array" });
        return utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]]);
      },
    };
  }

  const binaryDecoder = async (data: Uint8Array) => {
    // Always create a new Uint8Array to guarantee compatibility
    const safeData = new Uint8Array(data);
    const blob = new Blob([safeData], { type: mimeType });
    return URL.createObjectURL(blob);
  };

  if (mimeType === "application/pdf") {
    return {
      type: "pdf",
      decoder: binaryDecoder,
    };
  }

  const isSafariOnlyFormat = ["image/heic", "image/tiff"].includes(mimeType);
  if (isSafariOnlyFormat) {
    const ua = navigator.userAgent.toLowerCase();
    const isSafariBrowser =
      ua.includes("safari") &&
      !ua.includes("chrome") &&
      !ua.includes("firefox") &&
      !ua.includes("edge");

    if (!isSafariBrowser) {
      // HEIC/TIFF image formats not supported on non-Safari browsers
      return null;
    }
  }

  if (mimeType.startsWith("image/")) {
    return {
      type: "image",
      decoder: binaryDecoder,
    };
  }

  // File not previewable
  return null;
};

/**
 * Processes the response from fetching the file and converts it into a previewable format.
 *
 * @param response - The response object from the fetch request.
 * @param decode - decode data for consumption by FilePreview component.
 * @param onProgress - Callback to handle updating progress as file data is streamed
 * @returns A promise that resolves to the preview data or null if the file type is unsupported.
 */
const processResponseData = async (
  response: Response,
  decode: (data: Uint8Array) => Promise<string>,
  onProgress: (progress: number) => void
): Promise<string> => {
  let receivedLength = 0;
  const contentLength = Number(response.headers.get("Content-Length"));
  const data = new Uint8Array(contentLength);
  const reader = response.body?.getReader();

  if (!reader) {
    throw new Error("ReadableStream not supported or body is null.");
  }

  while (reader) {
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    if (value) {
      data.set(value, receivedLength);
      const prevProgress = Math.floor((receivedLength / contentLength) * 100);
      receivedLength += value.length;

      // Calculate and report progress
      const currentProgress = Math.floor((receivedLength / contentLength) * 100);

      if (currentProgress > prevProgress) {
        // Only update state if it's a whole % difference
        onProgress(prevProgress);
      }
    }
  }

  return decode(data);
};

/**
 * Generates previews for different file types in the browser.
 *
 * Supports:
 * - Text files (source code, CSV, JSON, YAML)
 * - PDFs
 * - Images
 * - Microsoft Word documents
 * - Excel spreadsheets
 *
 * Features:
 * - MIME type detection using magic bytes and file extension
 * - Download progress tracking
 * - Size limit enforcement (200MB)
 *
 * @param {PreviewFile} file - Object containing file key and size
 * @returns {PreviewResponse} Preview state with loading, error, progress and data properties
 *
 * @example
 * const { loading, data } = useFilePreview(file);
 */
const useFilePreview = (file: PreviewFile): PreviewResponse => {
  const [preview, setPreview] = useState<PreviewResponse>({
    loading: true,
  });
  // Memoized prop so that renaming while preview is open doesn't trigger a reload
  const memoizedFile = useMemo(() => file, []);

  const onProgress = (progress: number) => setPreview((prev) => ({ ...prev, progress: progress }));

  const loadFilePreview = async (file: PreviewFile, signal: AbortSignal) => {
    try {
      setPreview({ loading: true });

      if (file.size === 0) {
        return setPreview({ loading: false, error: "No preview available" });
      }

      if (Number(file.size) > MAX_SIZE) {
        return setPreview({ loading: false, error: "File too large to preview in browser" });
      }

      // Generate S3 direct download (pre-signed) URL
      const s3UrlResp = await fetch(
        `${VITE_RWB_API}/file-browser/generate-download-url?preview=true`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ key: file.key }),
        }
      );

      if (!s3UrlResp.ok) {
        throw new Error("Unable to generate download URL for preview");
      }

      const presignedDownloadUrl = await s3UrlResp.json();

      if (!presignedDownloadUrl) {
        throw new Error("Unable to generate pre-signed URL");
      }

      const fileExtension = file.key.split(".").pop()?.toLowerCase() ?? "";
      // First, make a partial request to determine file MIME type
      const mimeType = await determineMimeType(presignedDownloadUrl, fileExtension);

      if (!mimeType) {
        // File is not a preview-able format, end early without requesting the rest of the file
        return setPreview({
          data: null,
          loading: false,
          error: "Unable to preview unknown file format",
        });
      }

      const previewHandler = getPreviewHandler(mimeType);

      if (!previewHandler) {
        return setPreview({ loading: false, error: `Unsupported file type: ${mimeType}` });
      }

      // Proceed to download full file since it's valid
      const response = await fetch(presignedDownloadUrl, {
        signal,
      });

      if (!response.ok) {
        throw new Error("Unable to download file for preview");
      }

      const previewData = await processResponseData(response, previewHandler.decoder, onProgress);

      setPreview({ data: { content: previewData, type: previewHandler.type }, loading: false });
    } catch (e: any) {
      if (e.name !== "AbortError") {
        console.error(e);
        monitoring?.recordError(e);
        setPreview({ loading: false, error: e.message || DEFAULT_ERROR_MESSAGE });
      }
    }
  };

  useEffect(() => {
    const abortController = new AbortController();

    if (memoizedFile) {
      loadFilePreview(memoizedFile, abortController.signal);
    }

    return () => {
      // Cancel any in progress download requests on unmount
      abortController.abort();

      if (import.meta.env.PROD && preview?.data) {
        // Handle cleaning up any potentially assigned object URLs on unmount
        // note: Skipping this in dev mode due to issues with ReactStrict mode double render
        URL.revokeObjectURL(preview.data.content);
      }
    };
  }, [memoizedFile]);

  return preview;
};

export default useFilePreview;
